import { useEffect, useState } from "react";

import moment from "moment";
import camelcaseKeys from "camelcase-keys";
import { useNavigate } from "react-router-dom";

import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import LinkIcon from "@mui/icons-material/Link";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import CloudIcon from "@mui/icons-material/Cloud";
import StorageIcon from "@mui/icons-material/Storage";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AttributionIcon from "@mui/icons-material/Attribution";
import { visuallyHidden } from "@mui/utils";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import {
  Box,
  Chip,
  Collapse,
  Container,
  IconButton,
  Switch,
  TableContainer,
  TablePagination,
  TableSortLabel,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import conf from "config-dlm";
import useOpen from "components/utils/useOpen";
import Refresh from "components/utils/Refresh";
import InfoChip from "components/utils/InfoChip";
import ErrorChip from "components/utils/ErrorChip";
import SuccessChip from "components/utils/SuccessChip";
import { useAuth } from "components/auth/AuthProvider";
import TagsWithEdit from "./Tags";
import usePagination from "hooks/pagination";
import EmptyTableRow from "components/rows/Empty";
import SectionHeader from "components/utils/SectionHeader";
import ProtectedRoute from "components/auth/ProtectedRoute";
import SwitchWithConfirm from "components/utils/SwitchWithConfirm";
import ButtonWithConfirm from "components/utils/ButtonWithConfirm";
import EmailRequestDialog from "components/utils/Request";
import LinearIndeterminate from "components/utils/LinearLoader";
import DescriptionWithEdit from "./Description";
import { usePavmRequestConnect } from "hooks/pavm/request";
import {
  usePavmConnect,
  ProjectSchema,
  usePavmDisconnect,
} from "hooks/pavm/base";
import {
  useUserServiceProjectsActivate,
  useUserServiceProjectsDeactivate,
  useUserServiceProjects,
} from "hooks/user/projects";
import { useSmartContent } from "components/utils/SmartCell";

const COLUMNS = conf.COLUMNS.PROJECTS;

const ProjectRow = ({
  config,
  handleConnect,
}: {
  config: ProjectSchema;
  handleConnect: (uri: string, projectId: string) => void;
}) => {
  const { id, tags, infrastructure, name, online, uri, user } = config;

  const portalVersion = config.portalVersion;
  let displayedVersion;
  let versionInfo;

  if (portalVersion) {
    displayedVersion = 'Custom';
    const regex = /^(\d{2}_\d{2})/;

    const match = portalVersion.match(regex);

    if (match) {
      displayedVersion = match[1];
    }

    versionInfo = useSmartContent(portalVersion, portalVersion.split('-')[0]);
    if (config.portalVersionUpdatedAt) {
      versionInfo += `\n Fetched ${moment(config.portalVersionUpdatedAt).fromNow()}`
    }
  }

  const navigate = useNavigate();
  const { user: self, setUser } = useAuth();

  const isAdmin = self.role === "admin";
  const isConnected = self.project.connected;
  const isMyProject = self.project.id === id;

  const connected = user.connected;
  const connectedUser = user.email;

  const MyProjectIcon = isMyProject ? (
    <AttributionIcon fontSize="small" color="success" />
  ) : (
    <></>
  );
  const enableDisconnect = isMyProject || !!(isAdmin && connected);

  const disconnectMessage = (
    <>
      Are you sure you want to disconnect <b>{connectedUser}</b> from
      <b> {name}</b>? <br />
      This action cannot be undone.
    </>
  );

  const deactivateMessage = (
    <>
      Are you sure you want to deactivate <b>{name}</b>? This will disconnect{" "}
      <b>{connectedUser}</b>. <br />
      This action cannot be undone.
    </>
  );

  const request = usePavmRequestConnect();
  const activate = useUserServiceProjectsActivate();
  const deactivate = useUserServiceProjectsDeactivate();
  const disconnect = usePavmDisconnect();

  const handleAccessChange = (checked: boolean) => {
    // if checked (true), activate project, else deactivate
    checked ? activate.mutate({ id }) : deactivate.mutate({ id });
  };

  const handleRequestConnect = (reason: string) =>
    request.mutate({ uri, project: name, reason });

  const handleDisconnect = (email: string, projectId: string) =>
    disconnect.mutate({ uri, email, projectId });

  useEffect(() => {
    if (disconnect.isSuccess) {
      const user = camelcaseKeys(disconnect.data.data.user, { deep: true });

      // if you unlock yourself, update user to enure home page reflects accurate 'active project' status
      if (self.email === user.email) setUser(user);
    }
  }, [disconnect.isSuccess]);

  return (
    <TableRow sx={conf.LAST_CHILD_NO_BORDER}>
      <TableCell scope="row">
        <InfoChip
          size="small"
          placement="left"
          label={name}
          info={infrastructure}
          icon={infrastructure === "hosted" ? <CloudIcon /> : <StorageIcon />}
        />
        <DescriptionWithEdit
          id={id}
          type="project"
          isAdmin={isAdmin}
          description={config.description}
        />
      </TableCell>

      <TableCell align="right">
        <TagsWithEdit id={id} tags={tags} isAdmin={isAdmin} />
      </TableCell>

      <TableCell align="right">
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          {displayedVersion ? displayedVersion : '--'}
          {displayedVersion && <InfoChip
            size="small"
            color="primary"
            info={versionInfo ? versionInfo : ''}
            icon={<EditIcon sx={{ fontSize: 18 }} />}
          />}
          </Box>
      </TableCell>

      <TableCell align="right">{moment(user.lastActive).fromNow()}</TableCell>

      <TableCell align="right">
        {connected ? (
          <Chip
            size="small"
            icon={MyProjectIcon}
            label={connectedUser}
            variant="filled"
          />
        ) : (
          "--"
        )}
      </TableCell>

      <TableCell align="right">
        {connected ? moment(user.lastActive).fromNow(true) : "--"}
      </TableCell>

      <TableCell align="right">
        {isAdmin ? (
          connected ? (
            <SwitchWithConfirm
              type="Deactivate"
              defaultChecked={online}
              message={deactivateMessage}
              onConfirm={() => deactivate.mutate({ id })}
            />
          ) : (
            <Switch
              color={online ? "success" : "error"}
              checked={online}
              onChange={(e, checked) => handleAccessChange(checked)}
            />
          )
        ) : online ? (
          <SuccessChip size="small" label={"active"} />
        ) : (
          <ErrorChip size="small" label={"inactive"} />
        )}
      </TableCell>

      <TableCell align="right">
        {isMyProject ? (
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate(`/projects/${name?.toLowerCase()}`)}
          >
            Resume
          </Button>
        ) : config.status === "AVAILABLE" ? (
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={!online || isConnected}
            onClick={() => handleConnect(uri, id)}
          >
            Connect
          </Button>
        ) : (
          <EmailRequestDialog
            fullWidth
            type="Connect"
            name={name}
            disabled={!online || isConnected}
            onSubmit={handleRequestConnect}
          />
        )}
      </TableCell>

      <TableCell align="right">
        {isMyProject ? (
          <Button
            color="error"
            variant="contained"
            disabled={!enableDisconnect}
            onClick={() => handleDisconnect(connectedUser, id)}
          >
            Disconnect
          </Button>
        ) : (
          <ButtonWithConfirm
            type="disconnect"
            label="disconnect"
            color="error"
            message={disconnectMessage}
            disabled={!enableDisconnect}
            onConfirm={() => handleDisconnect(connectedUser, id)}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

type ProjectsProps = { collapsible?: boolean };

export const Projects = ({ collapsible = false }: ProjectsProps) => {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const columns = isAdmin ? COLUMNS.ADMIN : COLUMNS.SUPPORT;

  const { open, toggle } = useOpen(true);
  const [name, setName] = useState<string>("");

  const paginate = usePagination({ defaultSort: "user.lastActive" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const connect = usePavmConnect();
  const projects = useUserServiceProjects({ ...body, name });

  const handleConnect = (uri: string, projectId: string) => {
    connect.mutate({ uri, projectId });
  };

  useEffect(() => {
    document.title = "GRACE | projects";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container maxWidth="xl" sx={{ marginBottom: 4 }}>
        <SectionHeader
          title="Projects"
          icon={<AccountTreeIcon />}
          buttons={[
            <TextField
              key={"projects-name-search"}
              type="text"
              size="small"
              placeholder="Search ..."
              value={name}
              onChange={(e) => setName(e.target.value)}
            />,
            <TablePagination
              key={"projects-pagination"}
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={projects?.data?.meta.total || 0}
              page={page}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh
              key={"projects-refresh"}
              onClick={projects.refetch}
              isFetching={projects.isFetching}
            />,
            ...(collapsible
              ? [
                  <IconButton
                    key={"projects-collapse"}
                    size="small"
                    onClick={toggle}
                    aria-label="expand"
                    sx={{
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s",
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>,
                ]
              : []),
          ]}
        />

        <Collapse in={open}>
          <TableContainer sx={{ boxShadow: 4, maxHeight: 630 }}>
            <Table aria-label="collapsible table" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.label}
                      sortDirection={order}
                      style={{ whiteSpace: "nowrap" }}
                      align={column.isHeader ? "left" : "right"}
                    >
                      <TableSortLabel
                        active={sort === column.key}
                        direction={sort === column.key ? order : "asc"}
                        onClick={(event: React.MouseEvent<unknown>) =>
                          handleSort(event, column.key)
                        }
                      >
                        <b>{column.label}</b>
                        {sort === column.key ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}

                  <TableCell align="right">
                    <LinkIcon fontSize="inherit" />
                  </TableCell>

                  <TableCell align="right">
                    <LockOpenOutlinedIcon fontSize="inherit" />
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {projects.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <LinearIndeterminate />
                    </TableCell>
                  </TableRow>
                ) : projects?.data?.data.length === 0 ? (
                  <EmptyTableRow type="projects" colSpan={8} />
                ) : (
                  projects?.data?.data.map((project) => (
                    <ProjectRow
                      key={project.id}
                      config={project}
                      handleConnect={handleConnect}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Container>
    </ProtectedRoute>
  );
};
